import { ILoginDto, IUserInfoDto } from "@kuika/kuika-cl-model";
import { AxiosResponse } from "axios";
import { FormInstance } from "rc-field-form";
import * as React from "react";
import { rememberAccount } from "../../auth/remembered-accounts";
import { FireBaseService } from "../../firebase/firebase";
import { UserService } from "../../services/user-service";
import { KNavigator } from "../../shared/hoc/with-history";
import { KMainFunctions } from "../../shared/utility/main-functions";
import { toastService } from "../ui-kit/kuika-toast/toast-service";

interface ISignInWindow extends Window {
  skipAlert?: boolean;
  userInfo?: IUserInfoDto;
}

declare let window: ISignInWindow;

/** Exact wording the E2E suite and returning users know; do not reword casually. */
export const INVALID_CREDENTIALS_MESSAGE: string = "The username or password is incorrect.";

/** `LoginResult.status` for an account whose email address was never confirmed. */
const LOGIN_STATUS_NOT_ACTIVATED: number = 2;

const isInvalidCredentials = (result: string | number): boolean =>
  typeof result === "string" && result.toLowerCase().includes("username or password");

/**
 * Where a successful sign-in lands. The standalone `login.html` bundle has no router into the app,
 * so it reloads `/` (which `auth/redirect.tsx` resolves to the last workspace); the SPA route
 * navigates in place.
 */
export const navigateAfterSignIn = (isLoginHTML: boolean, navigator: KNavigator): void => {
  if (isLoginHTML) {
    window.skipAlert = true;
    window.location.href = "/";
    return;
  }
  navigator.gotoRecent();
};

export const goToForgotPassword = (isLoginHTML: boolean, navigator: KNavigator): void => {
  if (isLoginHTML) {
    window.skipAlert = true;
    window.location.href = "/forgotpassword";
    return;
  }
  navigator.gotoForgotPassword();
};

export interface IUsePasswordSignInOptions {
  form: FormInstance<ILoginDto>;
  isLoginHTML: boolean;
  navigator: KNavigator;
}

export interface IPasswordSignIn {
  isSubmitting: boolean;
  isResending: boolean;
  /** Set when the last attempt was refused because the address is not confirmed yet. */
  emailToResend: string | undefined;
  submit: (values: ILoginDto) => Promise<void>;
  resendVerificationEmail: () => Promise<void>;
  /** Wire to the form's `onValuesChange`: typing again takes the credentials error away. */
  clearCredentialsError: () => void;
}

/**
 * Email + password sign-in, shared by the plain form and a remembered account's password step so
 * both treat every server answer the same way.
 */
export const usePasswordSignIn = ({ form, isLoginHTML, navigator }: IUsePasswordSignInOptions): IPasswordSignIn => {
  const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false);
  const [isResending, setIsResending] = React.useState<boolean>(false);
  const [emailToResend, setEmailToResend] = React.useState<string | undefined>(undefined);
  const hasCredentialsError: React.MutableRefObject<boolean> = React.useRef<boolean>(false);

  const submit: (values: ILoginDto) => Promise<void> = React.useCallback(
    async (values: ILoginDto): Promise<void> => {
      setIsSubmitting(true);
      setEmailToResend(undefined);
      // GetUserInfo answers from `window.userInfo` whenever it already holds someone, so a person who
      // came back to /login from an open session would otherwise sign in as B and be handed A's
      // identity (and A would be remembered). The Google path clears it the same way.
      window.userInfo = undefined;

      let result: string | number;
      try {
        result = await UserService.Login(values, isLoginHTML);
      } catch (error: unknown) {
        // UserService.Login turns its own failures into a returned message; this only catches one
        // that escapes it, so the button never stays spinning — and never reads as success.
        if (!isLoginHTML) {
          KMainFunctions.exceptionHandler(error);
        }
        result = error instanceof Error && error.message.length > 0 ? error.message : "Login failed";
      }

      if (result === "") {
        // Remembered here, at the sign-in site — never inside UserService.Login. The profile's
        // change-password dialog reuses Login, and GetUserInfo runs on every boot and token
        // handoff; none of those is a person choosing to sign in on this device.
        rememberAccount(window.userInfo, "password");
        navigateAfterSignIn(isLoginHTML, navigator);
        // Still "submitting" on purpose: the page is changing, and a second click must not post again.
        return;
      }

      setIsSubmitting(false);

      if (result === LOGIN_STATUS_NOT_ACTIVATED) {
        toastService.error("You should activate your account before signing in");
        setEmailToResend(values.username);
        return;
      }

      if (typeof result === "string" && result.length > 0) {
        new FireBaseService().logEvent("login_error", { error: result });
      }

      if (isInvalidCredentials(result)) {
        hasCredentialsError.current = true;
        form.setFields([{ name: "password", errors: [INVALID_CREDENTIALS_MESSAGE] }]);
        return;
      }

      toastService.error(typeof result === "string" && result.length > 0 ? result : "Login failed");
    },
    [form, isLoginHTML, navigator]
  );

  const resendVerificationEmail: () => Promise<void> = React.useCallback(async (): Promise<void> => {
    if (isResending || emailToResend === undefined) {
      return;
    }
    setIsResending(true);

    const response: AxiosResponse | undefined = await UserService.SendConfirmationEmail(emailToResend);
    if (response?.status === 200) {
      if (isLoginHTML) {
        window.skipAlert = true;
        window.location.href = "/email-confirmation";
      } else {
        navigator.goToEmailConfirmation();
      }
      return;
    }

    setIsResending(false);
  }, [emailToResend, isLoginHTML, isResending, navigator]);

  const clearCredentialsError: () => void = React.useCallback((): void => {
    if (!hasCredentialsError.current) {
      return;
    }
    hasCredentialsError.current = false;
    form.setFields([{ name: "password", errors: [] }]);
  }, [form]);

  return { isSubmitting, isResending, emailToResend, submit, resendVerificationEmail, clearCredentialsError };
};
